﻿import type { Strings } from './strings.en.js';

/**
 * French UI strings.
 *
 * Translated for register rather than word for word. The product's whole job is to make
 * an awkward money conversation feel calm, so the French uses "vous" throughout and
 * avoids the clipped imperative that would read as brusque.
 *
 * Two terms are fixed vocabulary and used consistently everywhere:
 *   "plafond commun"     the group-safe cap
 *   "montant confortable" the comfortable maximum
 */
export const fr: Strings = {
  common: {
    skipToContent: 'Aller au contenu principal',
    loading: 'Chargement…',
    tryAgain: 'Réessayer',
    close: 'Fermer',
    perPerson: 'par personne',
    navHowItWorks: 'Comment ça marche',
    navPrivacy: 'Confidentialité',
    navFaq: 'Questions',
    navGuides: 'Guides',
    navBlog: 'Articles',
    homeLink: 'Accueil',
    language: 'Langue',
    chooseLanguage: 'Choisir une langue',
    amountInCurrency: (code: string) => `Montant en ${code}`,
    navLandmarkMain: 'Principale',
    navLandmarkFooter: 'Pied de page',
  },

  landing: {
    heroHeadline: 'Fixez un budget sans que personne ne révèle le sien.',
    heroSupport:
      'Créez un lien privé. Chacun indique le maximum qu’il serait à l’aise de dépenser. Vous ne voyez que le montant qui convient à tout le groupe.',
    previewTitle: 'Ce que le groupe voit',
    previewNote: 'Arrondi à la baisse à partir du montant confortable le plus bas.',
    previewExampleNote: 'Exemple uniquement. Aucun salon n’a été créé.',
  },

  createForm: {
    legend: 'Créer un budget de groupe privé',
    titleLabel: 'Pour quoi établissez-vous ce budget ?',
    titlePlaceholder: 'Week-end à Lisbonne',
    titleHint: 'Toute personne ayant le lien le voit. N’y mettez rien de personnel.',
    peopleLabel: 'Combien de personnes ?',
    peopleHint: 'Entre 3 et 30, vous compris.',
    currencyLabel: 'Devise',
    amountLabel: 'Votre montant confortable par personne',
    amountHint: 'Le maximum que vous seriez content de dépenser. Ni promesse, ni engagement.',
    submit: 'Créer le budget privé',
    submitting: 'Création de votre lien privé…',
    trustLine: 'Votre montant n’est jamais montré au groupe.',
    errorTitleRequired: 'Ajoutez une courte description pour que chacun sache de quoi il s’agit.',
    errorTitleTooLong: 'Restez sous 80 caractères.',
    errorPeopleRange:
      'Choisissez entre 3 et 30 personnes. Trois est le minimum pour préserver la confidentialité.',
    errorAmountRequired: 'Indiquez le maximum que vous seriez à l’aise de dépenser.',
    errorAmountInvalid: 'Saisissez un montant sous forme de nombre, par exemple 300.',
    errorAmountTooPrecise: 'Cette devise n’utilise pas autant de décimales.',
    errorAmountTooLarge: 'Ce montant est trop élevé.',
    errorGeneric:
      'La création du salon a échoué. Vos informations sont toujours là, vous pouvez réessayer.',
    errorRateLimited: 'Cela fait beaucoup de salons en peu de temps. Attendez une minute et réessayez.',
    errorTurnstile:
      'Nous n’avons pas pu vérifier ce navigateur. Rechargez la page et réessayez.',
    amountEcho: (formatted: string) => `Soit ${formatted}`,
  },

  share: {
    title: 'Votre lien de budget privé est prêt.',
    support:
      'Envoyez-le à toutes les personnes concernées. Le résultat apparaît quand toutes les réponses attendues sont arrivées.',
    linkLabel: 'Lien du salon privé',
    copy: 'Copier le lien',
    copied: 'Lien copié',
    copyFailed: 'Copie automatique impossible. Sélectionnez le lien ci-dessus et copiez-le.',
    share: 'Partager le lien',
    shareTitle: 'Budget de groupe privé',
    shareText:
      'Indiquez le maximum que vous seriez à l’aise de dépenser par personne. Votre montant reste privé.',
    manageTitle: 'Conservez votre lien d’organisateur',
    manageBody:
      'L’adresse de cette page est aussi votre lien d’organisateur. Mettez-la en favori. C’est le seul moyen de modifier la taille du groupe, de réinitialiser ou de supprimer le salon, et elle est irrécupérable.',
    manageWarning:
      'N’envoyez pas le lien d’organisateur au groupe. Envoyez plutôt le lien ci-dessus.',
    continue: 'Aller au salon',
  },

  room: {
    subtitle: 'Budget de groupe privé',
    question: 'Quel est le maximum que vous seriez à l’aise de dépenser par personne ?',
    questionSupport: 'Seul le plafond commun final sera partagé.',
    amountLabel: 'Votre montant confortable par personne',
    submit: 'Envoyer en privé',
    submitting: 'Enregistrement…',
    update: 'Modifier mon montant',
    edit: 'Modifier mon montant',
    cancelEdit: 'Annuler',
    savedTitle: 'Réponse enregistrée. Vous pouvez la modifier jusqu’à la clôture du salon.',
    yourAmount: 'Votre montant',
    privacyTitle: 'Privé par conception',
    privacyPoints: [
      'Ni nom ni e-mail',
      'Votre montant n’est jamais montré',
      'Les montants bruts sont supprimés après le résultat',
    ],
    privacySentence: 'Votre montant n’est jamais montré au groupe.',
    progressLabel: (answered: number, expected: number) =>
      `${answered} personne(s) sur ${expected} ont répondu`,
    progressAccessible: (answered: number, expected: number) =>
      `Progression : ${answered} réponses reçues sur ${expected} attendues. Aucun nom n’est enregistré.`,
    editHint: 'Vous pouvez modifier votre réponse jusqu’à la clôture du salon.',
    waitingTitle: 'En attente du reste du groupe',
    waitingBody: 'Le plafond commun s’affichera ici dès que tout le monde aura répondu.',
    refresh: 'Vérifier les mises à jour',
    revealing: 'Calcul du plafond commun…',
    fullTitle: 'Ce budget est déjà complet.',
    fullBody:
      'Aucune réponse supplémentaire ne peut être ajoutée. Demandez le résultat à l’organisateur.',
    errorSubmit:
      'Votre montant n’a pas été enregistré. Rien n’a été transmis au groupe, vous pouvez réessayer.',
    errorAmountInvalid: 'Saisissez un montant sous forme de nombre, par exemple 300.',
    errorAmountRequired: 'Indiquez le maximum que vous seriez à l’aise de dépenser.',
    errorRateLimited: 'Trop de tentatives. Patientez un instant, puis renvoyez.',
    errorConflict:
      'Le salon a changé pendant votre saisie. Nous l’avons actualisé, vérifiez avant d’envoyer.',
  },

  result: {
    heading: 'Le groupe peut prévoir sans risque',
    eyebrow: 'PLAFOND COMMUN',
    meaningTitle: 'Ce que cela signifie',
    explanation:
      'Ce montant est arrondi à la baisse et ne dépasse jamais le montant confortable le plus bas indiqué.',
    privacyTitle: 'Confidentialité',
    deletion: 'Les réponses individuelles n’ont jamais été montrées et ont maintenant été supprimées.',
    repeatCta: 'En créer un pour votre groupe',
    footnote: 'Aucun nom · Aucun compte · Salons temporaires',
    availableUntil: (date: string) =>
      `Ce résultat reste disponible jusqu’au ${date}, puis il est supprimé.`,
  },

  manage: {
    title: 'Commandes de l’organisateur',
    subtitle: 'Visible uniquement par vous, depuis votre lien d’organisateur.',
    peopleLabel: 'Nombre de personnes attendues',
    increaseHelp: 'Vous pouvez l’augmenter à tout moment avant l’affichage du résultat.',
    decreaseBlocked:
      'Vous ne pouvez plus le réduire maintenant qu’une autre personne a répondu. Réinitialisez plutôt le salon.',
    save: 'Mettre à jour la taille du groupe',
    saving: 'Mise à jour…',
    saved: 'Taille du groupe mise à jour.',
    resetTitle: 'Réinitialiser le salon',
    resetBody: 'Supprime toutes les réponses, y compris la vôtre, et repart de zéro.',
    reset: 'Réinitialiser le salon',
    resetConfirm:
      'Réinitialiser ce salon ? Toutes les réponses, y compris la vôtre, seront supprimées immédiatement.',
    resetting: 'Réinitialisation…',
    resetDone: 'Salon réinitialisé. Toutes les réponses ont été supprimées.',
    deleteTitle: 'Supprimer le salon',
    deleteBody: 'Supprime immédiatement le salon et toutes les réponses. Le lien cesse de fonctionner.',
    delete: 'Supprimer le salon',
    deleteConfirm:
      'Supprimer définitivement ce salon ? Toutes les réponses seront supprimées immédiatement et le lien cessera de fonctionner.',
    deleting: 'Suppression…',
    deletedTitle: 'Salon supprimé.',
    deletedBody: 'Le salon et tous les montants envoyés ont été supprimés.',
    errorGeneric: 'Cela n’a pas fonctionné. Rechargez votre lien d’organisateur et réessayez.',
    errorAuth: 'Ce lien d’organisateur n’est pas valide pour ce salon.',
  },

  states: {
    notFoundTitle: 'Ce lien ne fonctionne pas.',
    notFoundBody:
      'Le salon a peut-être été supprimé par son organisateur, ou le lien est incomplet. Vérifiez le lien que vous avez reçu.',
    expiredTitle: 'Ce salon de budget privé a expiré et les montants envoyés ont été supprimés.',
    expiredBody: 'Les salons durent 7 jours. Rien n’est conservé de celui-ci.',
    createNew: 'Créer un nouveau budget',
    genericErrorTitle: 'Une erreur est survenue.',
    genericErrorBody: 'Le salon n’a pas pu être chargé. Vérifiez votre connexion et réessayez.',
    offline: 'Vous semblez être hors ligne.',
  },

  footer: {
    tagline: 'Une façon discrète, pour un groupe, de convenir d’une limite de dépense.',
    rights: 'Aucun compte. Aucun nom. Salons temporaires.',
    madeBy: (maker: string) => `Réalisé par ${maker}`,
    groupTrips: 'Voyages en groupe',
    groupGifts: 'Cadeaux communs',
  },

  ads: {
    label: 'Publicité',
    inactive: (slot: string) => `Espace publicitaire réservé (${slot}), inactif`,
  },
};
